import type { PatientMedicationResponse } from '../../services/api';
import { parseISOToLocalDate } from '../../utils/dateUtils';
import { getHttpStatus } from '../../utils/httpErrorUtils';

const CREATE_REVIEW_SELECTION_ERROR =
  'The selected medications need review. Review your selections and try again.';
const CREATE_RETRY_ERROR =
  'Unable to create medication order. Please try again.';
const CREATE_PERMISSION_ERROR =
  'You do not have permission to create this medication order.';

export interface MedicationOrderEligibility {
  eligible: boolean;
  reason?: string;
}

export function getMedicationOrderCreateErrorMessage(
  errorResponse: unknown
): string {
  const status = getHttpStatus(errorResponse);
  if (status === 403) {
    return CREATE_PERMISSION_ERROR;
  }
  if (status === 400 || status === 409 || status === 422) {
    return CREATE_REVIEW_SELECTION_ERROR;
  }
  return CREATE_RETRY_ERROR;
}

export function getMedicationOrderEligibility(
  medication: PatientMedicationResponse,
  orderDateIso: string
): MedicationOrderEligibility {
  const orderDate = parseISOToLocalDate(orderDateIso);
  const startDate = parseISOToLocalDate(medication.start_date);
  const discontinueDate = parseISOToLocalDate(medication.discontinue_date);

  if (!orderDate || !startDate) {
    return { eligible: false, reason: 'Missing medication dates' };
  }

  if (startDate > orderDate) {
    return { eligible: false, reason: 'Starts after order date' };
  }

  if (discontinueDate && discontinueDate < orderDate) {
    return { eligible: false, reason: 'Discontinued before order date' };
  }

  return { eligible: true };
}
